import {
	Badge as BadgeIcon,
	Block as BlockIcon,
	Business as BusinessIcon,
	CheckCircle as CheckCircleIcon,
	Check as CheckIcon,
	Delete,
	Drafts as DraftsIcon,
	Elderly as ElderlyIcon,
	Home as HomeIcon,
	Mail as MailIcon,
	Markunread as MarkunreadIcon,
	Report as ReportIcon,
	SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
	createPersonEmailByPersonId,
	deletePersonEmailByPersonId,
	PersonEmail,
	updatePersonEmailAddress,
} from "../../api/personemail/person-email";
import { formatDateTime } from "../../utils/date-formatting";

const PersonEmailTable = ({
	personId,
	personEmails,
	emailsEditable,
	compact = false,
}: {
	personId: number;
	personEmails: PersonEmail[];
	emailsEditable: boolean;
	compact?: boolean;
}) => {
	const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
	const [personEmailsState, setPersonEmailsState] = useState<PersonEmail[]>([]);
	const [personEmail, setPersonEmail] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
	const [emailToDelete, setEmailToDelete] = useState<PersonEmail | null>(null);
	const [toggleConfirmOpen, setToggleConfirmOpen] = useState<boolean>(false);
	const [emailToToggle, setEmailToToggle] = useState<PersonEmail | null>(null);

	useEffect(() => {
		setPersonEmailsState(personEmails);
	}, [personEmails]);

	const handleOpenAddDialog = () => {
		setPersonEmail("");
		setError("");
		setAddDialogOpen(true);
	};

	const handleCloseAddDialog = () => {
		setAddDialogOpen(false);
		setPersonEmail("");
		setError("");
	};

	const handleDeleteClick = (email: PersonEmail) => {
		setEmailToDelete(email);
		setDeleteConfirmOpen(true);
	};

	const handleDeleteConfirm = () => {
		if (!emailToDelete?.id) return;
		deletePersonEmailByPersonId(personId, emailToDelete.id)
			.then(() => {
				const newPersonEmails = personEmailsState.filter(
					(email) => email.id !== emailToDelete.id
				);
				setPersonEmailsState(newPersonEmails);
				setDeleteConfirmOpen(false);
				setEmailToDelete(null);
			})
			.catch((err) => {
				console.log(err);
				setDeleteConfirmOpen(false);
				setEmailToDelete(null);
			});
	};

	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false);
		setEmailToDelete(null);
	};

	const handleSaveEmail = () => {
		const validEmail = validateEmail(personEmail);
		if (!validEmail) {
			setError("Invalid Email!");
			return;
		}
		setLoading(true);
		createPersonEmailByPersonId(personId, personEmail)
			.then((addedEmail) => {
				setPersonEmailsState([...personEmailsState, addedEmail]);
				setLoading(false);
				handleCloseAddDialog();
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSaveEmail();
		}
	};

	const validateEmail = (email: string) => {
		return String(email)
			.toLowerCase()
			.match(
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
			);
	};

	const handleToggleClick = (email: PersonEmail) => {
		setEmailToToggle(email);
		setToggleConfirmOpen(true);
	};

	const handleToggleCancel = () => {
		setToggleConfirmOpen(false);
		setEmailToToggle(null);
	};

	const handleToggleConfirm = () => {
		if (!emailToToggle) return;
		const target = emailToToggle;
		const updatedEmail = {
			...target,
			used: !target.used,
			person: personId,
		};
		updatePersonEmailAddress(personId, target.id, updatedEmail).then(() => {
			setPersonEmailsState((prev) =>
				prev.map((item) => (item.id === target.id ? updatedEmail : item))
			);
			setToggleConfirmOpen(false);
			setEmailToToggle(null);
		});
	};

	return (
		<Box sx={{ p: 1 }} data-component="email-table-container">
			<Grid sx={{ overflowX: "auto" }} data-component="email-table-grid">
				<Table aria-label="simple table" data-component="email-table">
					<TableHead data-component="email-table-head">
						<TableRow data-component="email-table-header-row">
							<TableCell
								sx={{ p: 1 }}
								data-component="header-icons"
							></TableCell>
							<TableCell sx={{ p: 1 }} data-component="header-priority">
								Prio
							</TableCell>
							{!compact && (
								<TableCell sx={{ p: 1 }} data-component="header-source">
									Source
								</TableCell>
							)}
							<TableCell sx={{ p: 1 }} data-component="header-email">
								Email
							</TableCell>
							{!compact && (
								<TableCell
									sx={{ p: 1 }}
									data-component="header-created"
								>
									Created
								</TableCell>
							)}
							{!compact && (
								<TableCell
									sx={{ p: 1 }}
									data-component="header-updated"
								>
									Updated
								</TableCell>
							)}
							<TableCell
								sx={{ p: 1 }}
								data-component="header-used"
							></TableCell>
							{!compact && (
								<TableCell sx={{ p: 1 }} data-component="header-delete">
									Delete
								</TableCell>
							)}
						</TableRow>
					</TableHead>
					<TableBody data-component="email-table-body">
						{personEmailsState.map((row) => (
							<TableRow
								key={row.email + row.id}
								data-component="email-row"
								sx={{
									backgroundColor: row.best_emailperson
										? (theme) => theme.highlights.experience
										: row.unopened ||
												row.x_failed_recipients ||
												row.obsolete ||
												row.auto_submitted
											? (theme) => theme.highlights.language
											: null,
									"&:last-child td, &:last-child th": {
										border: 0,
									},
								}}
							>
								<TableCell
									sx={{ p: 1 }}
									data-component="email-icons-cell"
								>
									{row.category_str === "PROFESSIONAL" ? (
										<Tooltip
											title="Work Email"
											data-component="work-email-tooltip"
										>
											<BusinessIcon data-component="work-email-icon" />
										</Tooltip>
									) : row.category_str === "PERSONAL" ? (
										<Tooltip
											title="Personal Email"
											data-component="personal-email-tooltip"
										>
											<HomeIcon data-component="personal-email-icon" />
										</Tooltip>
									) : null}
									{row.best_emailperson ? (
										<Tooltip
											title="Best Email"
											data-component="best-email-tooltip"
										>
											<CheckCircleIcon
												color="primary"
												data-component="best-email-icon"
											/>
										</Tooltip>
									) : null}
									{row.has_opened ? (
										<Tooltip
											title="Email was opened"
											data-component="opened-email-tooltip"
										>
											<DraftsIcon data-component="opened-email-icon" />
										</Tooltip>
									) : null}
									{row.unopened ? (
										<Tooltip
											title="Unopened"
											data-component="unopened-tooltip"
										>
											<MarkunreadIcon data-component="unopened-icon" />
										</Tooltip>
									) : null}
									{row.x_failed_recipients ? (
										<Tooltip
											title="Email delivery failed"
											data-component="failed-tooltip"
										>
											<ReportIcon data-component="failed-icon" />
										</Tooltip>
									) : null}
									{row.auto_submitted ? (
										<Tooltip
											title="Email has automated response"
											data-component="autoresponse-tooltip"
										>
											<SmartToyIcon data-component="autoresponse-icon" />
										</Tooltip>
									) : null}
									{row.obsolete ? (
										<Tooltip
											title="obsolete"
											data-component="obsolete-tooltip"
										>
											<ElderlyIcon data-component="obsolete-icon" />
										</Tooltip>
									) : null}
									{row.current ? (
										<Tooltip
											title="Current Employer"
											data-component="current-employer-tooltip"
										>
											<BadgeIcon data-component="current-employer-icon" />
										</Tooltip>
									) : null}
								</TableCell>
								<TableCell sx={{ p: 1 }} data-component="priority-cell">
									{row.priority}
								</TableCell>
								{!compact && (
									<TableCell
										sx={{ p: 1 }}
										data-component="source-cell"
									>
										{row.source_name}
									</TableCell>
								)}
								<TableCell
									sx={{
										p: 1,
										textDecoration: row.used
											? "none"
											: "line-through",
										color: row.used ? "inherit" : "text.disabled",
									}}
									data-component="email-cell"
								>
									{row.email}
								</TableCell>
								{!compact && (
									<TableCell
										sx={{ p: 1 }}
										data-component="created-cell"
									>
										{formatDateTime(row.created)}
									</TableCell>
								)}
								{!compact && (
									<TableCell
										sx={{ p: 1 }}
										data-component="updated-cell"
									>
										{formatDateTime(row.updated)}
									</TableCell>
								)}
								<TableCell sx={{ p: 1 }} data-component="used-cell">
									<Button
										size="small"
										variant="outlined"
										color="primary"
										startIcon={
											row.used ? <BlockIcon /> : <CheckIcon />
										}
										onClick={() => handleToggleClick(row)}
										data-component="email-toggle-used-button"
									>
										{row.used ? "Disable" : "Enable"}
									</Button>
								</TableCell>
								{!compact && (
									<TableCell
										sx={{ p: 1 }}
										data-component="delete-cell"
									>
										<IconButton
											sx={{ p: 0 }}
											onClick={(e) => handleDeleteClick(row)}
											data-component="delete-email-button"
										>
											<Delete data-component="delete-icon" />
										</IconButton>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Grid>
			{/* Add Email button */}
			{emailsEditable && (
				<Stack
					direction="row"
					justifyContent="flex-end"
					sx={{ mt: 2 }}
					data-component="add-email-button-stack"
				>
					<Button
						variant="outlined"
						size="small"
						startIcon={<MailIcon />}
						onClick={handleOpenAddDialog}
						data-component="add-email-button"
					>
						Add Email
					</Button>
				</Stack>
			)}

			{/* Add Email Dialog */}
			<Dialog
				open={addDialogOpen}
				onClose={handleCloseAddDialog}
				maxWidth="xs"
				fullWidth
				data-component="add-email-dialog"
			>
				<DialogTitle data-component="add-dialog-title">Add Email</DialogTitle>
				<DialogContent data-component="add-dialog-content">
					<TextField
						fullWidth
						size="small"
						placeholder="Enter email address..."
						value={personEmail}
						onChange={(e) => {
							setPersonEmail(e.target.value);
							setError("");
						}}
						onKeyDown={handleKeyDown}
						error={!!error}
						helperText={error}
						autoFocus
						sx={{ mt: 1 }}
						data-component="email-input"
					/>
				</DialogContent>
				<DialogActions data-component="add-dialog-actions">
					<Button
						onClick={handleCloseAddDialog}
						data-component="cancel-add-button"
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveEmail}
						disabled={loading || !personEmail.trim()}
						data-component="save-email-button"
					>
						{loading ? <CircularProgress size={20} /> : "Add"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteConfirmOpen}
				onClose={handleDeleteCancel}
				data-component="delete-email-confirmation-dialog"
			>
				<DialogTitle data-component="dialog-title">
					Delete Email Address?
				</DialogTitle>
				<DialogContent data-component="dialog-content">
					<DialogContentText data-component="dialog-text">
						Are you sure you want to delete{" "}
						<strong>{emailToDelete?.email}</strong>? This action cannot be
						undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions data-component="dialog-actions">
					<Button
						onClick={handleDeleteCancel}
						color="inherit"
						data-component="cancel-button"
					>
						Cancel
					</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						variant="contained"
						data-component="confirm-delete-button"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={toggleConfirmOpen}
				onClose={handleToggleCancel}
				data-component="toggle-email-confirmation-dialog"
			>
				<DialogTitle data-component="toggle-dialog-title">
					{emailToToggle?.used
						? "Disable Email Address?"
						: "Enable Email Address?"}
				</DialogTitle>
				<DialogContent data-component="toggle-dialog-content">
					<DialogContentText data-component="toggle-dialog-text">
						{emailToToggle?.used ? (
							<>
								Disabling <strong>{emailToToggle?.email}</strong> means
								outgoing email to this person will no longer use this
								address. The address stays on file and can be re-enabled
								later. Any in-flight messages already queued may still
								be delivered.
							</>
						) : (
							<>
								Enabling <strong>{emailToToggle?.email}</strong> makes
								this address available again for outgoing messages. It
								may become the "best email" used for new sends if no
								higher-priority address exists.
							</>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions data-component="toggle-dialog-actions">
					<Button
						onClick={handleToggleCancel}
						color="inherit"
						data-component="cancel-toggle-button"
					>
						Cancel
					</Button>
					<Button
						onClick={handleToggleConfirm}
						color="primary"
						variant="contained"
						startIcon={emailToToggle?.used ? <BlockIcon /> : <CheckIcon />}
						data-component="confirm-toggle-button"
					>
						{emailToToggle?.used ? "Disable" : "Enable"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PersonEmailTable;
